import React, { useMemo } from 'react';
import cn from 'clsx';
import { ChevronDownIcon, ChevronUpIcon } from '@fronton/icons-react';
import { Lazy, Mousewheel } from 'swiper';
import { getImageSrc, Swiper } from 'common/components';
import { FreeMode, SwiperClass, SwiperSlide, Thumbs } from 'common/components/swiper';
import { createElementKey } from 'utils';
import {
  activePreviewSlide,
  chevronIconHidden,
  ChevronIconWrapper,
  previewSlide,
  previewSwiper,
  previewWrapper,
  previewWrapperWithoutChevron,
} from './preview-styles';

type PreviewProps = {
  activeImageIndex: number;
  imgAlt: string;
  landingName: string;
  mediaPhoto: string[];
  onPreviewClick: (index: number) => () => void;
  setThumbsSwiper: (swiper: SwiperClass) => void;
};

export const Preview = ({
  activeImageIndex,
  imgAlt,
  landingName,
  mediaPhoto,
  onPreviewClick,
  setThumbsSwiper,
}: PreviewProps) => {
  const isChevronIconVisible = useMemo(() => mediaPhoto.length > 4, [mediaPhoto]);

  const handlePrevPreviewButtonClick = () => {
    if (activeImageIndex === 0) {
      return;
    }
    const index = activeImageIndex - 1;

    onPreviewClick(index)();
  };

  const handleNextPreviewButtonClick = () => {
    if (activeImageIndex === mediaPhoto.length - 1) {
      return;
    }

    const index = activeImageIndex + 1;

    onPreviewClick(index)();
  };

  return (
    <div className={cn(previewWrapper, { [previewWrapperWithoutChevron]: !isChevronIconVisible })}>
      {isChevronIconVisible && (
        <ChevronIconWrapper
          className={cn({ [chevronIconHidden]: activeImageIndex === 0 })}
          onClick={handlePrevPreviewButtonClick}>
          <ChevronUpIcon type="outline" size="m" strokeWidth={3} color="control-primary" />
        </ChevronIconWrapper>
      )}
      <Swiper
        className={previewSwiper}
        direction="vertical"
        modules={[Lazy, FreeMode, Mousewheel, Thumbs]}
        onSwiper={setThumbsSwiper}
        mousewheel
        slidesPerView="auto"
        watchOverflow
        lazy>
        {mediaPhoto.map((image, index: number) => (
          <SwiperSlide
            className={cn(previewSlide, {
              [activePreviewSlide]: activeImageIndex === index,
            })}
            onClick={onPreviewClick(index)}>
            <img
              alt={imgAlt}
              height={48}
              key={createElementKey('imagePreview', index)}
              src={getImageSrc(
                `${landingName}${image}`,
                {
                  width: 48,
                  height: 48,
                  objectFit: 'fit',
                },
                true
              )}
              title={imgAlt}
              width={48}
              loading="lazy"
            />
          </SwiperSlide>
        ))}
      </Swiper>
      {isChevronIconVisible && (
        <ChevronIconWrapper
          className={cn({ [chevronIconHidden]: activeImageIndex === mediaPhoto.length - 1 })}
          onClick={handleNextPreviewButtonClick}>
          <ChevronDownIcon type="outline" strokeWidth={3} size="m" color="control-primary" />
        </ChevronIconWrapper>
      )}
    </div>
  );
};
